import React, { FunctionComponent, useEffect, useState } from "react";
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Fade,
  List,
  ListItem,
  ListItemText,
  Modal
} from "@material-ui/core";
import useStyles from "./styles";
import { Alert, AlertTitle } from "@material-ui/lab";
import { DropzoneArea } from "material-ui-dropzone";
import {
  CampaignProductSelectionType,
  SetupCampaign
} from "../../models/Campaign";
import { uploadCampaignProducts } from "../../api/campaignApi";
import { isOk } from "../../result";
import { useTheme } from "@material-ui/core/styles";
import { useI18n } from "../../hooks/useI18n";

interface Props {
  campaign: SetupCampaign;
  onSave: () => void;
}

export const CampaignProductUpload: FunctionComponent<Props> = ({
  campaign,
  onSave
}) => {
  const classes = useStyles();
  const theme = useTheme();
  const { t } = useI18n();

  const [state, setState] = useState<any>({
    uploading: false,
    uploadFinished: false,
    result: null
  });

  useEffect(() => {
    if (state.uploading) {
      uploadCampaignProducts(state.file, campaign).then(result => {
        if (isOk(result)) {
          setState({
            ...state,
            uploading: false,
            uploaded: true,
            result: result.value
          });

          if (result.value.length == 0) return onSave();

          return;
        }

        return setState({
          ...state,
          uploading: false,
          uploaded: false,
          result: result.details
        });
      });
    }
  }, [state.uploading]);

  const handleUpload = () => {
    return setState({
      ...state,
      uploading: true,
      file: state.file,
      result: { validationErrors: [], syncErrors: [], syncResult: [] }
    });
  };

  const hasSyncResult = (): boolean => {
    return !!(state.result && state.result.length && state.result.length > 0);
  };

  return (
    <>
      <Alert severity="info">
        {campaign.productSelectionType ===
        CampaignProductSelectionType.SelectedProducts ? (
          <>
            {t("common.createCsvFileWithFormat")}
            {t("common.gtinPriceMinAmountMinQuantityMaxQuantity")}
            <p>
              {t("common.fillInGtinAndPrice")}
              <br />
              <i>{t("common.leaveMinAmountMinQuantityMaxQuantityEmpty")}</i>
            </p>
          </>
        ) : (
          <>
            {t("common.createCsvFileWithFormat")}
            {t("common.gtinPriceNumberOfCombosComboGroupId")}
            <p>
              {t("common.fillInGtinPriceNumberOfCombosComboGroupId")}
              <br />
              <i>{t("common.allProductsContainingSameComboGroupId")}</i>
            </p>
          </>
        )}

        <p>{t("common.uploadYourFile")}</p>
        <p>{t("common.pressUploadCampaignProducts")}</p>
      </Alert>
      <div className={classes.fileUploadArea}>
        <DropzoneArea
          acceptedFiles={[".csv, text/csv"]}
          dropzoneText={t("common.dragAndDropUpdatedPriceFile")}
          showPreviewsInDropzone={true}
          useChipsForPreview
          filesLimit={1}
          onChange={files => {
            setState({
              ...state,
              file: files[files.length - 1],
              uploaded: false,
              result: null
            });
          }}
        />
      </div>
      {state.file ? (
        <>
          <div className={classes.uploadButtons}>
            <Button variant="contained" color="primary" onClick={handleUpload}>
              {t("common.uploadCampaignProducts")}
            </Button>
          </div>
        </>
      ) : null}

      {!state.uploaded && state.result && state.result.status ? (
        <Alert severity="error">
          <AlertTitle>
            {state.result.status}, {state.result.title}
          </AlertTitle>
          {state.result.detail} <strong>{state.result.traceId}</strong>
        </Alert>
      ) : null}

      {state.uploaded ? (
        <div className={classes.resultContainer}>
          {hasSyncResult() ? (
            <Alert severity={"warning"}>
              <AlertTitle style={{ fontWeight: "bold" }}>
                Campaign has been created without the following products
                containing errors
              </AlertTitle>
              <List>
                {state.result.map((x: string, i: number) => (
                  <ListItem key={i}>
                    <ListItemText
                      color={theme.palette.error.dark}
                      primary={x}
                    />
                  </ListItem>
                ))}
              </List>
            </Alert>
          ) : null}
        </div>
      ) : null}

      <Modal
        open={state.uploading}
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center"
        }}
      >
        <Fade in={state.uploading}>
          <Card>
            <CardHeader
              title={"Uploading campaign products..."}
              titleTypographyProps={{ variant: "h5" }}
            />
            <CardContent className={classes.loading}>
              <CircularProgress />
            </CardContent>
          </Card>
        </Fade>
      </Modal>
    </>
  );
};
